import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { PastasRepository } from '../../data/repositories/PastasRepository';
import { ResponsiblesRepository } from '../../data/repositories/ResponsiblesRepository';
import {
  formatHabitaDateInput,
  HabitaErrorNotice,
  HabitaPagination,
  isHabitaDateInputValue,
  resolveHabitaDateRange,
} from '../../shared/components/habita';
import type { HabitaDatePeriod, HabitaDateRangeFilterValue } from '../../shared/components/habita';
import {
  PASTA_STATUS_LABELS,
  PASTA_STATUS_ORDER,
} from '../../shared/habitaStatusPresentation';
import type { HabitaProcessListItem, HabitaResponsible, PastaStatus } from '../../shared/models';
import {
  DEFAULT_PROCESS_SORT_ORDER,
  type ProcessSortOrder,
} from '../../shared/utils/processOrdering';
import {
  getSdrResponsibleEmail,
  isSdrScopedUser,
} from '../../shared/utils';
import {
  PastasCardGrid,
  PastasEmptyState,
  PastasLoadingState,
  PastasTable,
  PastasToolbar,
  type PastasViewMode,
} from './components/PastasWidgets';

const ALL = '__all__';
const DEFAULT_PAGE_SIZE = 10;
const PAGE_SIZE_OPTIONS = [10, 20, 50, 100];
const PASTAS_FILTERS_STORAGE_KEY = 'habita4u_pastas_filters';
const PASTAS_FILTERS_STORAGE_VERSION = 2;
const DEFAULT_PASTAS_STATUS: PastaStatus = 'RECEBIMENTO_DOCS';

const dateFilterPeriods: HabitaDatePeriod[] = [
  'today',
  'yesterday',
  '7d',
  '30d',
  '90d',
  'month',
  'previous_month',
  'previous_quarter',
  'year',
  'custom',
];

const defaultDateFilterValue: HabitaDateRangeFilterValue = {
  customEnd: '',
  customStart: '',
  period: '30d',
};

interface PersistedPastasFilters {
  dateFilter: HabitaDateRangeFilterValue;
  version?: number;
  pageSize: number;
  processoTerm: string;
  responsavel: string;
  searchTerm: string;
  sortOrder: ProcessSortOrder;
  status: string;
  viewMode: PastasViewMode;
}

const defaultPastasFilters: PersistedPastasFilters = {
  dateFilter: defaultDateFilter(),
  version: PASTAS_FILTERS_STORAGE_VERSION,
  pageSize: DEFAULT_PAGE_SIZE,
  processoTerm: '',
  responsavel: ALL,
  searchTerm: '',
  sortOrder: DEFAULT_PROCESS_SORT_ORDER,
  status: DEFAULT_PASTAS_STATUS,
  viewMode: 'tabela',
};

function defaultDateFilter(): HabitaDateRangeFilterValue {
  return { ...defaultDateFilterValue };
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return value !== null && typeof value === 'object' && !Array.isArray(value);
}

function persistedString(value: unknown, fallback: string): string {
  return typeof value === 'string' ? value : fallback;
}

function persistedSortOrder(value: unknown): ProcessSortOrder {
  return value === 'asc' || value === 'desc' ? value : DEFAULT_PROCESS_SORT_ORDER;
}

function persistedViewMode(value: unknown): PastasViewMode {
  return value === 'cards' || value === 'tabela' ? value : 'tabela';
}

function persistedPageSize(value: unknown): number {
  return typeof value === 'number' && PAGE_SIZE_OPTIONS.includes(value)
    ? value
    : DEFAULT_PAGE_SIZE;
}

function persistedPastasStatus(value: unknown): string {
  return typeof value === 'string' && (value === ALL || PASTA_STATUS_ORDER.includes(value as PastaStatus))
    ? value
    : DEFAULT_PASTAS_STATUS;
}

function persistedDateFilter(value: unknown): HabitaDateRangeFilterValue {
  if (!isRecord(value)) return defaultDateFilter();

  const savedPeriod = value.period;
  if (!dateFilterPeriods.includes(savedPeriod as HabitaDatePeriod)) return defaultDateFilter();

  if (savedPeriod === 'custom') {
    const customStart = value.customStart;
    const customEnd = value.customEnd;
    if (!isHabitaDateInputValue(customStart) || !isHabitaDateInputValue(customEnd) || customStart > customEnd) {
      return defaultDateFilter();
    }

    return {
      customEnd,
      customStart,
      period: 'custom',
    };
  }

  return {
    customEnd: '',
    customStart: '',
    period: savedPeriod as HabitaDatePeriod,
  };
}

function readPersistedPastasFilters(): PersistedPastasFilters {
  if (typeof window === 'undefined') return defaultPastasFilters;

  try {
    const parsed = JSON.parse(window.sessionStorage.getItem(PASTAS_FILTERS_STORAGE_KEY) ?? 'null');
    if (!isRecord(parsed)) return defaultPastasFilters;

    const shouldUsePersistedStatus = parsed.version === PASTAS_FILTERS_STORAGE_VERSION;

    return {
      dateFilter: persistedDateFilter(parsed.dateFilter),
      version: PASTAS_FILTERS_STORAGE_VERSION,
      pageSize: persistedPageSize(parsed.pageSize),
      processoTerm: persistedString(parsed.processoTerm, ''),
      responsavel: persistedString(parsed.responsavel, ALL),
      searchTerm: persistedString(parsed.searchTerm, ''),
      sortOrder: persistedSortOrder(parsed.sortOrder),
      status: shouldUsePersistedStatus ? persistedPastasStatus(parsed.status) : DEFAULT_PASTAS_STATUS,
      viewMode: persistedViewMode(parsed.viewMode),
    };
  } catch {
    return defaultPastasFilters;
  }
}

function persistPastasFilters(filters: PersistedPastasFilters) {
  if (typeof window === 'undefined') return;

  try {
    window.sessionStorage.setItem(
      PASTAS_FILTERS_STORAGE_KEY,
      JSON.stringify({ ...filters, version: PASTAS_FILTERS_STORAGE_VERSION })
    );
  } catch {
    // Sessão pode estar indisponível em alguns contextos de navegador.
  }
}

function responsibleOptionLabel(responsible: HabitaResponsible): string {
  return responsible.name || responsible.email;
}

function buildResponsibleOptions(
  responsibles: HabitaResponsible[],
  selectedResponsible: string
): { label: string; value: string }[] {
  const options = responsibles
    .filter((responsible) => responsible.email || responsible.name)
    .map((responsible) => ({
      label: responsibleOptionLabel(responsible),
      value: responsible.email || responsible.name,
    }));

  if (
    selectedResponsible !== ALL &&
    selectedResponsible.trim() &&
    !options.some((option) => option.value === selectedResponsible)
  ) {
    options.push({ label: selectedResponsible, value: selectedResponsible });
  }

  return options;
}

export default function Pastas() {
  const pastasRepository = useMemo(() => new PastasRepository(), []);
  const responsiblesRepository = useMemo(() => new ResponsiblesRepository(), []);
  const [searchParams, setSearchParams] = useSearchParams();
  const globalQuery = searchParams.get('q') ?? '';
  const persistedFilters = useMemo(() => readPersistedPastasFilters(), []);
  const isResponsibleScoped = useMemo(() => isSdrScopedUser(), []);
  const responsibleScopeEmail = useMemo(() => getSdrResponsibleEmail(), []);
  const [items, setItems] = useState<HabitaProcessListItem[]>([]);
  const [responsibles, setResponsibles] = useState<HabitaResponsible[]>([]);
  const [searchTerm, setSearchTerm] = useState(globalQuery || persistedFilters.searchTerm);
  const [processoTerm, setProcessoTerm] = useState(persistedFilters.processoTerm);
  const [responsavel, setResponsavel] = useState(isResponsibleScoped ? ALL : persistedFilters.responsavel);
  const [status, setStatus] = useState(persistedFilters.status);
  const [sortOrder, setSortOrder] = useState<ProcessSortOrder>(persistedFilters.sortOrder);
  const [viewMode, setViewMode] = useState<PastasViewMode>(persistedFilters.viewMode);
  const [dateFilter, setDateFilter] = useState<HabitaDateRangeFilterValue>(persistedFilters.dateFilter);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(persistedFilters.pageSize);
  const [totalCount, setTotalCount] = useState(0);
  const [hasExplicitTotal, setHasExplicitTotal] = useState(true);
  const [hasNextPage, setHasNextPage] = useState(false);
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState(globalQuery || persistedFilters.searchTerm);
  const [debouncedProcessoTerm, setDebouncedProcessoTerm] = useState(persistedFilters.processoTerm);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const mountedRef = useRef(false);
  const requestSequenceRef = useRef(0);
  const loadingRef = useRef(true);
  const lastGlobalQueryRef = useRef(globalQuery);

  const totalPages = hasExplicitTotal
    ? Math.max(1, Math.ceil(totalCount / pageSize))
    : Math.max(1, page + (hasNextPage ? 1 : 0));
  const currentPage = Math.min(page, totalPages);
  const requestRange = useMemo(() => resolveHabitaDateRange(dateFilter), [dateFilter]);
  const maxSelectableDate = useMemo(() => formatHabitaDateInput(new Date()), []);

  useEffect(() => {
    mountedRef.current = true;

    return () => {
      mountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    if (isResponsibleScoped) {
      setResponsibles([]);
      return undefined;
    }

    let cancelled = false;

    async function loadResponsibles() {
      try {
        const nextResponsibles = await responsiblesRepository.fetchResponsibles();
        if (!cancelled) {
          setResponsibles(nextResponsibles);
        }
      } catch {
        if (!cancelled) {
          setResponsibles([]);
        }
      }
    }

    void loadResponsibles();

    return () => {
      cancelled = true;
    };
  }, [isResponsibleScoped, responsiblesRepository]);

  const loadPastas = useCallback(
    async ({ silent = false }: { silent?: boolean } = {}) => {
      if (silent && loadingRef.current) return;

      const requestId = requestSequenceRef.current + 1;
      requestSequenceRef.current = requestId;

      try {
        if (!silent) {
          loadingRef.current = true;
          setLoading(true);
        }
        setError(null);
        if (isResponsibleScoped && !responsibleScopeEmail) {
          setError('Não foi possível identificar o responsável do usuário logado.');
          setItems([]);
          setHasExplicitTotal(true);
          setHasNextPage(false);
          setTotalCount(0);
          return;
        }

        const requestedResponsibleEmail = isResponsibleScoped
          ? responsibleScopeEmail ?? ''
          : responsavel !== ALL ? responsavel : '';
        const result = await pastasRepository.fetchPastasPage({
          page,
          page_size: pageSize,
          startDate: requestRange.startDate,
          endDate: requestRange.endDate,
          ...(status !== ALL ? { status: status as PastaStatus } : {}),
          ...(debouncedSearchTerm.trim() ? { searchText: debouncedSearchTerm } : {}),
          ...(debouncedProcessoTerm.trim() ? { processoText: debouncedProcessoTerm } : {}),
          ...(requestedResponsibleEmail ? { responsibleEmail: requestedResponsibleEmail } : {}),
          sortOrder,
        });
        if (!mountedRef.current || requestSequenceRef.current !== requestId) return;

        const loadedCount = (page - 1) * pageSize + result.items.length;
        setHasExplicitTotal(result.hasExplicitTotal);
        setHasNextPage(result.hasNextPage);
        setTotalCount(result.hasExplicitTotal ? result.total : Math.max(result.total, loadedCount));
        setItems(result.items);
      } catch (loadError) {
        if (!mountedRef.current || requestSequenceRef.current !== requestId) return;

        console.error('Erro ao carregar pastas:', loadError);
        setError('Não foi possível carregar as pastas.');
        if (!silent) {
          setItems([]);
        }
      } finally {
        if (!silent && mountedRef.current && requestSequenceRef.current === requestId) {
          loadingRef.current = false;
          setLoading(false);
        }
      }
    },
    [
      debouncedProcessoTerm,
      debouncedSearchTerm,
      isResponsibleScoped,
      page,
      pageSize,
      pastasRepository,
      responsavel,
      responsibleScopeEmail,
      requestRange.endDate,
      requestRange.startDate,
      sortOrder,
      status,
    ]
  );

  useEffect(() => {
    void loadPastas();
  }, [loadPastas]);

  useEffect(() => {
    persistPastasFilters({
      dateFilter: {
        customEnd: dateFilter.period === 'custom' ? dateFilter.customEnd : '',
        customStart: dateFilter.period === 'custom' ? dateFilter.customStart : '',
        period: dateFilter.period,
      },
      pageSize,
      processoTerm,
      responsavel: isResponsibleScoped ? ALL : responsavel,
      searchTerm,
      sortOrder,
      status,
      viewMode,
    });
  }, [dateFilter.customEnd, dateFilter.customStart, dateFilter.period, isResponsibleScoped, pageSize, processoTerm, responsavel, searchTerm, sortOrder, status, viewMode]);

  useEffect(() => {
    if (lastGlobalQueryRef.current === globalQuery) return;
    lastGlobalQueryRef.current = globalQuery;
    setSearchTerm(globalQuery);
    setDebouncedSearchTerm(globalQuery);
    setPage(1);
  }, [globalQuery]);

  useEffect(() => {
    const timeoutId = window.setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
      setDebouncedProcessoTerm(processoTerm);
    }, 400);

    return () => window.clearTimeout(timeoutId);
  }, [processoTerm, searchTerm]);

  useEffect(() => {
    if (hasExplicitTotal && page > totalPages) {
      setPage(totalPages);
    }
  }, [hasExplicitTotal, page, totalPages]);

  const responsavelOptions = useMemo(
    () => isResponsibleScoped ? [] : buildResponsibleOptions(responsibles, responsavel),
    [isResponsibleScoped, responsibles, responsavel]
  );

  const statusOptions = useMemo(
    () =>
      PASTA_STATUS_ORDER.map((pastaStatus) => ({
        label: PASTA_STATUS_LABELS[pastaStatus],
        value: pastaStatus,
      })),
    []
  );

  const hasFilters =
    searchTerm.trim() !== '' ||
    processoTerm.trim() !== '' ||
    (!isResponsibleScoped && responsavel !== ALL) ||
    status !== DEFAULT_PASTAS_STATUS ||
    dateFilter.period !== '30d' ||
    sortOrder !== DEFAULT_PROCESS_SORT_ORDER;
  const resultCountLabel = hasExplicitTotal ? String(totalCount) : `${totalCount}${hasNextPage ? '+' : ''}`;

  function resetPage() {
    setPage(1);
  }

  function changePageSize(nextPageSize: number) {
    setPageSize(nextPageSize);
    setPage(1);
  }

  function clearFilters() {
    setSearchTerm('');
    setProcessoTerm('');
    setResponsavel(ALL);
    setStatus(DEFAULT_PASTAS_STATUS);
    setSortOrder(DEFAULT_PROCESS_SORT_ORDER);
    setDateFilter(defaultDateFilter());
    lastGlobalQueryRef.current = '';
    setSearchParams({}, { replace: true });
    resetPage();
  }

  return (
    <section className="w-full space-y-4">
      <PastasToolbar
        hasFilters={hasFilters}
        onClearFilters={clearFilters}
        dateFilter={dateFilter}
        maxDateInput={maxSelectableDate}
        onDateFilterApply={(nextDateFilter) => {
          setDateFilter(nextDateFilter);
          resetPage();
        }}
        onDateFilterClear={() => {
          setDateFilter(defaultDateFilter());
          resetPage();
        }}
        onProcessoChange={(value) => {
          setProcessoTerm(value);
          resetPage();
        }}
        onResponsavelChange={(value) => {
          setResponsavel(value);
          resetPage();
        }}
        searchTerm={searchTerm}
        processoTerm={processoTerm}
        responsavel={responsavel}
        responsaveis={responsavelOptions}
        resultCount={totalCount}
        resultCountLabel={resultCountLabel}
        showResponsavelFilter={!isResponsibleScoped}
        status={status}
        statusOptions={statusOptions}
        onStatusChange={(value) => {
          setStatus(value);
          resetPage();
        }}
        sortOrder={sortOrder}
        onSortOrderChange={(value) => {
          setSortOrder(value);
          resetPage();
        }}
        viewMode={viewMode}
        onSearchChange={(value) => {
          setSearchTerm(value);
          if (globalQuery) {
            lastGlobalQueryRef.current = '';
            setSearchParams({}, { replace: true });
          }
          resetPage();
        }}
        onViewModeChange={setViewMode}
      />

      {error && (
        <HabitaErrorNotice>{error}</HabitaErrorNotice>
      )}

      {loading ? (
        <PastasLoadingState />
      ) : items.length === 0 ? (
        <PastasEmptyState searchTerm={searchTerm} />
      ) : viewMode === 'tabela' ? (
        <>
          <PastasTable items={items} />
          <HabitaPagination
            currentPage={currentPage}
            onPageChange={setPage}
            onPageSizeChange={changePageSize}
            pageSize={pageSize}
            pageSizeOptions={PAGE_SIZE_OPTIONS}
            totalItems={totalCount}
            totalItemsLabel={resultCountLabel}
            totalPages={totalPages}
          />
        </>
      ) : (
        <>
          <PastasCardGrid items={items} />
          <HabitaPagination
            currentPage={currentPage}
            onPageChange={setPage}
            onPageSizeChange={changePageSize}
            pageSize={pageSize}
            pageSizeOptions={PAGE_SIZE_OPTIONS}
            totalItems={totalCount}
            totalItemsLabel={resultCountLabel}
            totalPages={totalPages}
          />
        </>
      )}
    </section>
  );
}
